// src/App.js

import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { BrowserRouter as Router, Routes, Route, useParams } from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";

import logo from "./assets/logo.svg";

import TranslationForm from "./components/TranslationForm";
import LocaleSwitcher from "./components/LocaleSwitcher";
import "./App.css";

import "./i18n";
import { useTranslation } from "react-i18next";
import { updateHreflangTags }  from './utils/updateHreflangTags';
import { analyticsFetch } from './analytics';
import AnalyticsConsent from './components/AnalyticsConsent';

function AppContent() {
  const { t } = useTranslation();

  const [translatedSentences, setTranslatedSentences] = useState([]);
  const [languages, setLanguages] = useState(null);
  const [ttsLanguages, setTtsLanguages] = useState(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const { i18n } = useTranslation();
  const { lang } = useParams();
  
  const abortControllerRef = useRef(null);

  const StructuredData = ({ t }) => (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Majbyr",
          "url": window.location.origin,
        })}
      </script>
    </Helmet>
  );

  useEffect(() => {
    const currentLang = lang || i18n.language;
    const languages = [{ code: 'en' }, { code: 'ru' }, { code: 'kv' }, { code: 'udm' }];
    updateHreflangTags(currentLang, languages);
  }, [lang, i18n.language]);

  useEffect(() => {
    document
      .querySelector('meta[name="description"]')
      .setAttribute("content", t("description"));
    document.title = `${t("domain")} ${t("app")}`;
  }, [t]);

  
  useEffect(() => {
    setIsLoading(true);
    const loadData = async () => {
      await getTranslationLanguages();
      await getTtsLanguages();
      setIsLoading(false);
    };
    loadData();
  }, []);

  const handleComplexTranslation = async (text, src, tgt) => {
    if (text.trim() === "") {
      setTranslatedSentences([]);
      return;
    }

    // Abort the previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const response = await analyticsFetch(
        (process.env.REACT_APP_API_URL || "/api") + "/translate_by_sentences/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            text,
            src,
            tgt,
          }),
          signal: abortController.signal,
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();
      setTranslatedSentences(data.translations);
    } catch (error) {
      if (error.name !== "AbortError") {
        setTranslatedSentences([[[t("Failed to translate")]]]);
      }
    }
  };

  const handleTts = async (text, lang, setIsAudioPlaying) => {
    if (text.trim() === "") {
      return;
    }
    try {
      setIsAudioPlaying(true);
      const response = await analyticsFetch((process.env.REACT_APP_API_URL || "/api") + "/tts/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, lang }),
      });
      if (!response.ok) {
        setIsAudioPlaying(false);
        throw new Error("Response from TTS service was not okay");
      }

      // The response is an audio stream. We need to convert it to a blob.
      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);

      // Create a new HTMLAudioElement and play the audio
      const audio = new Audio(audioUrl);

      audio.onended = () => {
        setIsAudioPlaying(false);
      };
      audio.play();
      return audio;
    } catch (error) {
      console.error("Error in text-to-speech:", error);
      setIsAudioPlaying(false);
    }
  };

  // get translation languages from api
  const getTranslationLanguages = async () => {
    try {
      const response = await analyticsFetch(
        (process.env.REACT_APP_API_URL || "/api") + "/translation_languages/"
      );
      const data = await response.json();
      setLanguages(data.languages);
    } catch (error) {
      console.error("Error fetching languages:", error);
    }
  };

  const getTtsLanguages = async () => {
    try {
      const response = await analyticsFetch(
        (process.env.REACT_APP_API_URL || "/api") + "/tts_languages/"
      );
      const data = await response.json();
      setTtsLanguages(data.languages);
    } catch (error) {
      console.error("Error fetching languages:", error);
    }
  };

  if (isLoading) {
    return;
  }

  return (
    <Router>
      <div className="app">
        <AnalyticsConsent />
        <StructuredData t={t} />
        <header className="app-header">
          <img src={logo} className="app-logo" alt="logo" />
          <h1><span className="domain-name">{t("domain")}</span><span className="app-name">{t("app")}</span></h1>
          <LocaleSwitcher />
      
        </header>
        <Routes>
          <Route
            path="/:src/:tgt"
            element={
              <TranslationForm
                onTranslate={handleComplexTranslation}
                onTts={(text, lang) => handleTts(text, lang, setIsAudioPlaying)}
                languages={languages}
                ttsLanguages={ttsLanguages}
                translatedSentences={translatedSentences}
                setTranslatedSentences={setTranslatedSentences}
                isAudioPlaying={isAudioPlaying}
                setIsAudioPlaying={setIsAudioPlaying}
              />
            }
          />
          <Route
            path="/:locale"
            element={
              <TranslationForm
                onTranslate={handleComplexTranslation}
                onTts={(text, lang) => handleTts(text, lang, setIsAudioPlaying)}
                languages={languages}
                ttsLanguages={ttsLanguages}
                translatedSentences={translatedSentences}
                isAudioPlaying={isAudioPlaying}
                setIsAudioPlaying={setIsAudioPlaying}
              />
            }
          />
          <Route
            path="/"
            element={
              <TranslationForm
                onTranslate={handleComplexTranslation}
                onTts={(text, lang) => handleTts(text, lang, setIsAudioPlaying)}
                languages={languages}
                ttsLanguages={ttsLanguages}
                translatedSentences={translatedSentences}
                isAudioPlaying={isAudioPlaying}
                setIsAudioPlaying={setIsAudioPlaying}
              />
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
